import { ChevronLeft, ChevronRight, ShieldCheck, User } from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'
import { useState } from 'react'
import { toast } from 'sonner'

import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui'
import { cn } from '../../lib/utils'
import { logo } from '../../components/ui/logo'

interface BreadcrumbItem {
  label: string
  onClick?: () => void
  imageUrl?: string
  hideImage?: boolean
  fallbackImage?: React.ReactNode
}

interface NavbarProps {
  breadcrumbs?: BreadcrumbItem[]
  hideBack?: boolean
  appName: string
  tagText?: string
  tagIcon?: React.ReactNode
  onLogout?: () => Promise<void>
  onSettingsClick?: () => void
  userAvatar?: React.ReactNode
  className?: string
  showMaintenanceModeToggle?: boolean
}

export function Navbar({
  breadcrumbs = [],
  hideBack = false,
  appName,
  tagText = 'Admin Dashboard',
  tagIcon,
  onLogout,
  onSettingsClick,
  userAvatar,
  className,
  showMaintenanceModeToggle = false,
}: NavbarProps) {
  const navigate = useNavigate()
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set())

  const handleImageError = (imageUrl: string) => {
    setFailedImages((prev) => new Set([...prev, imageUrl]))
  }

  const LogoComponent = logo({ className: 'h-[28px] w-[28px]' })

  const Tag = ({ className: tagClassName }: { className?: string }) => {
    return (
      <div className="md:bg-white rounded-sm md:rounded-lg md:border  md:border-main">
        <div className={cn(`flex items-center p-1 md:p-2 md:bg-main/5 md:text-main`, tagClassName)}>
          {tagIcon || <ShieldCheck className="hidden md:block h-3 w-3" />}
          <span className="ml-0.5 md:ml-2.5 text-xs whitespace-nowrap leading-none">{tagText}</span>
        </div>
      </div>
    )
  }

  const handleLogout = async () => {
    if (onLogout) {
      try {
        await onLogout()
        toast.success('Logged out successfully')
      } catch (error) {
        toast.error('Error', {
          description: error instanceof Error ? error.message : 'Failed to logout',
        })
      }
    }
  }

  const handleSettings = () => {
    if (onSettingsClick) {
      onSettingsClick()
    }
  }

  return (
    <header
      className={cn(
        `flex justify-between w-full md:h-[110px] md:items-center gap-4 px-4 py-6 md:py-0 md:px-14 md:pt-[46px]
        min-h-[64px] transition-all duration-200`,
        className,
      )}
    >
      <nav className="hidden md:flex items-center shrink-0 flex-wrap md:flex-nowrap gap-2 order-2 md:order-1">
        <div className="flex-col gap-6 text-lg font-medium md:flex md:flex-row md:items-center md:gap-5 md:text-sm lg:gap-6 shrink-0">
          <Link
            to="/"
            className="flex items-center gap-2 text-lg font-semibold md:text-3xl shrink-0"
          >
            <LogoComponent />
            <div className="flex flex-col shrink-0">
              <span
                className={cn(
                  'font-bold',
                  breadcrumbs.length > 0
                    ? 'text-card-text hover:cursor-pointer hover:underline'
                    : '',
                )}
              >
                {appName}
              </span>
            </div>
          </Link>
        </div>
        {breadcrumbs.map((breadcrumb, index) => (
          <div key={index} className="flex items-center gap-2">
            <div className="text-3xl text-card-text">
              <ChevronRight className="h-4 w-4 md:h-6 md:w-6" />
            </div>
            <div className="flex items-center gap-4 shrink-0">
              <div className="flex items-center gap-2 shrink-0">
                {breadcrumb.imageUrl &&
                !failedImages.has(breadcrumb.imageUrl) &&
                !breadcrumb.hideImage ? (
                  <img
                    src={breadcrumb.imageUrl}
                    alt={`${breadcrumb.label} logo`}
                    className="hidden md:block w-6 h-6 md:w-[28px] md:h-[28px] rounded object-cover"
                    onError={() => handleImageError(breadcrumb.imageUrl!)}
                  />
                ) : !breadcrumb.hideImage ? (
                  <div className="hidden w-6 h-6 md:w-[28px] md:h-[28px] border border-card-border rounded-md bg-gray-200 md:flex items-center justify-center">
                    {breadcrumb.fallbackImage ? breadcrumb.fallbackImage : null}
                  </div>
                ) : (
                  <></>
                )}

                <h1
                  className={`text-lg md:text-3xl font-bold shrink-0 ${
                    breadcrumb.onClick ? 'hover:cursor-pointer hover:underline' : ''
                  } ${breadcrumbs.length - 1 === index ? '' : 'text-card-text'}`}
                  onClick={breadcrumb.onClick}
                >
                  {breadcrumb.label}
                </h1>
              </div>
            </div>
          </div>
        ))}
      </nav>
      {hideBack ? null : (
        <div className="flex gap-3 items-center md:hidden">
          <Button variant="main_action" className="h-8 w-8 rounded-md" onClick={() => navigate(-1)}>
            <ChevronLeft className="size-5" />
          </Button>
          <span className="font-semibold">Back</span>
        </div>
      )}
      <div
        className={`flex items-center gap-4 md:ml-auto md:gap-2 lg:gap-4 order-1 md:order-2 ${
          hideBack ? 'w-full justify-end' : ''
        }`}
      >
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <div className="rounded-full cursor-pointer transition-all duration-150 ease-in-out group">
              <div className="flex md:flex-row items-center gap-2">
                <Tag />
                <div className="h-8 w-8 rounded-full md:h-12 md:w-12 group-hover:translate-y-[-2px] group-hover:shadow-md transition-all duration-150 ease-in-out flex items-center justify-center bg-white border border-card-border">
                  {userAvatar || <User className="h-4 w-4 md:h-6 md:w-6" />}
                </div>
              </div>
              <span className="sr-only">Toggle user menu</span>
            </div>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {onSettingsClick && (
              <DropdownMenuItem onClick={handleSettings}>Settings</DropdownMenuItem>
            )}
            {onLogout && <DropdownMenuItem onClick={handleLogout}>Logout</DropdownMenuItem>}
            {showMaintenanceModeToggle && (
              <DropdownMenuItem onClick={() => navigate('/maintenance')}>
                Maintenance Mode
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  )
}
